import ContentCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Alert, Box, Button, CircularProgress, Grid, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { withStyles } from "@mui/styles";
import PropTypes from 'prop-types';
import { Component } from "react";
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import DialogGeneral from '../../../../components/dialog-general';
import CustomizedSnackbars from "../../../../components/material-snackbars/index.js";
import { AbilityContext } from '../../../../config/ability-context';
import * as WebchatService from "../../../../services/2talk/webchat.service.js";
import { Overlay } from "../../../../styles/global.js";
import { Styles } from "../../../../styles/material-styles";

class WebchatIdentityVerification extends Component {

  constructor(props) {
    super(props);
    const { webchats } = this.props;

    this.state = {
      webchatid: webchats.webchat.id,
      loading: false,
      loadingGrid: true,
      hasSecret: false,
      secret: '',
      showSecret: false,
      dialogRemoveOpen: false,
      openNotification: false,
      notificationVariant: 'success',
      notificationMessage: '',
    };
  }

  async componentDidMount() {
    await this.loadSecret();
  }

  loadSecret = async () => {
    const { intl } = this.props;
    try {
      this.setState({ loadingGrid: true });
      const output = await WebchatService.getIdentitySecret(this.state.webchatid);
      if (output && output.success) {
        this.setState({
          hasSecret: output.data?.hassecret ?? false,
          secret: output.data?.secret ?? '',
        });
      } else {
        this.notify('error', (output && output.errors && output.errors[0]) || intl.formatMessage({ id: "webchat.identity.notify.loaderror" }));
      }
    } catch (err) {
      this.notify('error', intl.formatMessage({ id: "webchat.identity.notify.loaderror" }));
    } finally {
      this.setState({ loadingGrid: false });
    }
  }

  generateSecret = async () => {
    const { intl } = this.props;
    try {
      this.setState({ loading: true });
      const output = await WebchatService.generateIdentitySecret(this.state.webchatid);
      if (output && output.success) {
        this.setState({
          secret: output.data?.secret ?? '',
          hasSecret: true,
          showSecret: true,
        });
        this.notify('success', intl.formatMessage({ id: "webchat.identity.notify.generated" }));
      } else {
        this.notify('error', (output && output.errors && output.errors[0]) || intl.formatMessage({ id: "webchat.identity.notify.generateerror" }));
      }
    } catch (err) {
      this.notify('error', intl.formatMessage({ id: "webchat.identity.notify.generateerror" }));
    } finally {
      this.setState({ loading: false });
    }
  }

  removeSecret = async () => {
    const { intl } = this.props;
    this.setState({ dialogRemoveOpen: false });
    try {
      this.setState({ loading: true });
      const output = await WebchatService.deleteIdentitySecret(this.state.webchatid);
      if (output && output.success) {
        this.setState({ secret: '', hasSecret: false, showSecret: false });
        this.notify('success', intl.formatMessage({ id: "webchat.identity.notify.removed" }));
      } else {
        this.notify('error', (output && output.errors && output.errors[0]) || intl.formatMessage({ id: "webchat.identity.notify.removeerror" }));
      }
    } catch (err) {
      this.notify('error', intl.formatMessage({ id: "webchat.identity.notify.removeerror" }));
    } finally {
      this.setState({ loading: false });
    }
  }

  copySecret = () => {
    const { intl } = this.props;
    if (!this.state.secret) return;
    navigator.clipboard.writeText(this.state.secret);
    this.notify('success', intl.formatMessage({ id: "webchat.identity.notify.copied" }));
  }

  notify = (variant, message) => {
    this.setState({ openNotification: true, notificationVariant: variant, notificationMessage: message });
  }

  closeNotification = () => this.setState({ openNotification: false });

  render() {
    const { classes, intl } = this.props;
    const { hasSecret, secret, showSecret, loadingGrid, loading } = this.state;

    return (
      <div style={{ marginTop: 20 }}>
        {loadingGrid ? (
          <CircularProgress style={{ margin: '40px auto', display: 'block' }} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VpnKeyIcon style={{ color: '#8BC34A' }} />
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "webchat.identity.title" })}
                </Typography>
              </Box>
              <Typography variant="body2" style={{ color: '#666', lineHeight: 1.6 }}>
                {intl.formatMessage({ id: "webchat.identity.description" })}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Alert severity={hasSecret ? 'success' : 'info'} style={{ marginBottom: 8 }}>
                {hasSecret
                  ? intl.formatMessage({ id: "webchat.identity.status.active" })
                  : intl.formatMessage({ id: "webchat.identity.status.inactive" })}
              </Alert>
            </Grid>

            {hasSecret && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: "webchat.identity.secret.label" })}
                  variant="outlined"
                  type={showSecret ? 'text' : 'password'}
                  value={secret}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showSecret ? intl.formatMessage({ id: "webchat.identity.tooltip.hide" }) : intl.formatMessage({ id: "webchat.identity.tooltip.show" })}>
                          <IconButton onClick={() => this.setState({ showSecret: !showSecret })} edge="end">
                            {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={intl.formatMessage({ id: "webchat.identity.tooltip.copy" })}>
                          <span>
                            <IconButton onClick={this.copySecret} edge="end" disabled={!secret}>
                              <ContentCopyIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />
                <Typography variant="caption" style={{ color: '#b26a00' }}>
                  ⚠️ {intl.formatMessage({ id: "webchat.identity.secret.warning" })}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button variant="contained" color="primary" onClick={this.generateSecret}>
                  {hasSecret
                    ? intl.formatMessage({ id: "webchat.identity.button.rotate" })
                    : intl.formatMessage({ id: "webchat.identity.button.generate" })}
                </Button>
                {hasSecret && (
                  <Button variant="outlined" color="secondary" onClick={() => this.setState({ dialogRemoveOpen: true })}>
                    {intl.formatMessage({ id: "webchat.identity.button.remove" })}
                  </Button>
                )}
              </Box>
              {hasSecret && (
                <Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 8 }}>
                  {intl.formatMessage({ id: "webchat.identity.rotate.hint" })}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}

        {loading && (
          <Overlay>
            <CircularProgress color='secondary' />
          </Overlay>
        )}

        <DialogGeneral
          dialogTitle={intl.formatMessage({ id: "webchat.identity.dialog.remove.title" })}
          dialogSubTitle={intl.formatMessage({ id: "webchat.identity.dialog.remove.subtitle" })}
          open={this.state.dialogRemoveOpen}
          openOrCloseModalFunction={() => this.setState({ dialogRemoveOpen: false })}
          handleConfirmation={this.removeSecret}
        />

        <CustomizedSnackbars
          variant={this.state.notificationVariant}
          message={this.state.notificationMessage}
          isOpen={this.state.openNotification}
          toClose={this.closeNotification}
        />
      </div>
    );
  }
}

WebchatIdentityVerification.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  userSession: state.userSession,
  webchats: state.webchats
});

export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(WebchatIdentityVerification)));
WebchatIdentityVerification.contextType = AbilityContext;
